import { useRef } from 'react';
import PropTypes from 'prop-types';

import Dialog from '@mui/material/Dialog';

import VideoInfo from 'components/UI/VideoInfo/VideoInfo';

/**
 * ShowTrailer component
 *
 * Displays a modal dialog with a video trailer for the course.
 * Uses Material-UI Dialog and VideoInfo for video playback.
 *
 * Props:
 * @param {boolean} openTrailer - Controls whether the trailer dialog is open.
 * @param {function} onClose - Callback to close the dialog.
 * @param {object} trailerData - Contains video source data (expects hlsMedium property).
 */

const ShowTrailer = ({ openTrailer, onClose, trailerData }) => {
  const playerRef = useRef(null);

  // Video.js options for the trailer video
  const videoJsOptions = {
    autoplay: true,
    currentTime: 20,
    controls: true,
    responsive: true,
    fluid: true,
    sources: [
      {
        src: trailerData?.hlsMedium
      }
    ]
  };

  /**
   * Callback when the video player is ready
   * @param {object} player - The video.js player instance
   */
  const handlePlayerReady = player => {
    playerRef.current = player;

    // Example player event listeners
    player.on('waiting', () => {
      // eslint-disable-next-line no-console
      console.log('player is waiting');
    });

    player.on('dispose', () => {
      // eslint-disable-next-line no-console
      console.log('player will dispose');
    });
  };

  return (
    <Dialog
      open={openTrailer} // Controls dialog visibility
      onClose={onClose} // Callback to close dialog
      fullWidth
      maxWidth="lg"
    >
      {/* Passes videoJsOptions and handlePlayerReady as props to VideoInfo */}
      <VideoInfo
        options={videoJsOptions}
        onReady={handlePlayerReady}
      />
    </Dialog>
  );
};

ShowTrailer.propTypes = {
  openTrailer: PropTypes.bool,
  onClose: PropTypes.func,
  trailerData: PropTypes.shape({
    hlsMedium: PropTypes.string
  })
};

ShowTrailer.defaultProps = {
  openTrailer: false,
  onClose: () => {},
  trailerData: null
};

export default ShowTrailer;
